import React, { useEffect, useState, useRef } from "react";

const POLL_INTERVAL_MS = 60 * 1000;
const DISMISS_KEY_PREFIX = "jobvious_banner_dismissed_";

async function fetchStatus() {
  const res = await fetch(`/maintenance.json?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function isBannerDismissed(id) {
  if (!id) return false;
  try {
    return localStorage.getItem(DISMISS_KEY_PREFIX + id) === "1";
  } catch {
    return false;
  }
}

function dismissBanner(id) {
  if (!id) return;
  try {
    localStorage.setItem(DISMISS_KEY_PREFIX + id, "1");
  } catch { /* no-op */ }
}

const MaintenanceWatcher = () => {
  const [banner, setBanner] = useState(null);
  const redirected = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    let cancelled = false;

    const tick = async () => {
      try {
        const data = await fetchStatus();
        if (cancelled) return;

        if (data.maintenance === true && !redirected.current) {
          redirected.current = true;
          const v = data.version ? `?v=${encodeURIComponent(data.version)}` : "";
          window.location.replace(`/maintenance.html${v}`);
          return;
        }

        if (data.banner && data.banner.enabled && data.banner.id && !isBannerDismissed(data.banner.id)) {
          setBanner(data.banner);
        } else {
          setBanner(null);
        }
      } catch {
        /* silent — don't disrupt the app if maintenance.json is briefly unavailable */
      }
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!banner) return null;

  const isWarning = banner.severity === "warning";
  const bg = isWarning ? "#ff9516" : "#3fb2be";
  const fg = isWarning ? "#1a1208" : "#041a1c";

  const handleDismiss = () => {
    dismissBanner(banner.id);
    setBanner(null);
  };

  return (
    <div
      role="status"
      style={{
        background: bg,
        color: fg,
        padding: "10px 16px",
        fontSize: 14,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 12,
        borderBottom: "1px solid rgba(0,0,0,.15)",
        boxShadow: "0 1px 0 rgba(0,0,0,.08)",
      }}
    >
      <i
        className={isWarning ? "fa-sharp fa-solid fa-triangle-exclamation" : "fa-sharp fa-solid fa-circle-info"}
        style={{ fontSize: 16, flexShrink: 0 }}
      />
      <span style={{ flex: 1, lineHeight: 1.4 }}>{banner.message}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: "1px solid rgba(0,0,0,.25)",
          color: fg,
          padding: "4px 10px",
          borderRadius: 4,
          fontWeight: 600,
          fontSize: 12,
          cursor: "pointer",
          letterSpacing: ".04em",
          textTransform: "uppercase",
        }}
      >
        Dismiss
      </button>
    </div>
  );
};

export default MaintenanceWatcher;
